import { getMinPrice } from '@shared/lib/money';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { QueryHookOptions } from '@apollo/react-hooks';
import {
	getAeroexpress,
	getAvailableGdsServicesByType,
	getBaggageDelivery,
	getCanAddUpgradeFlightClass,
	getGroundTransportationServices,
	getInsurances,
	getOrder,
	getPriorityBoarding,
	getSmsNotificationServices,
	getTravellers,
	getCapsuleHotel,
	getAnimalServices,
	getIsFfpSupported,
	getTravellerRefundableTicket,
	getSegments
} from '../../Checkout/store/order/selectors';
import {
	removeServiceAction,
	ServiceMeta,
	setMultipleSelectedServices,
	setServiceAction
} from '../../Checkout/store/selectedServices/actions';
import { ServiceType } from '../../Checkout/components/Checkout/OrderPaidServiceCards/types';
import { getPassengerLabels, getPassengerState } from '../../Checkout/store/passengers/selectors';
import { getPassengerStateCompatible, getSelectedServicesState } from '../../Checkout/store/selectedServices/selectors';
import {
	AviaPassengerType,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	OrderAdditionalServiceGdsServiceServiceType,
	PassengerFragment,
	BaggageFragment,
	useAdditionalServicesLazyQuery,
	useSeatMapLazyQuery,
	AdditionalServicesQuery,
	CheckinPassengerFragment
} from '@websky/graphql';
import { getPassengerPricesMap, getVipServicesToDelete } from '../utils';
import { Renderers, ServicePassengerSelected } from '../../VipServiceSelect';
import { SelectedService } from './PetPopup/hooks';
import { MoneyType } from '../../PriceMatrix/types';
import { isUnconfirmedService } from '../../utils';
import { getAnimalTotalPrice } from './utils';
import { CartServiceGds, OnDeleteItemType } from '../../Cart/types';
import { useIataCode } from '../../hooks';
import { fillOrder } from '../../Checkout/store/order/actions';
import { ServiceOpenEvent } from '../../Checkout/types';
import { WEBSKY_SERVICE_OPEN_EVENT } from '../../Checkout/constants';
import { CombinedSelectedServiceType } from '../../AIPurchasePredictionService/types';
import PassengerPriceControl from './PassengerPriceControl/PassengerPriceControl';
import PassengerPrices from './PassengerPrices/PassengerPrices';
import { getIsLoadingServiceByParams } from '../../Checkout/store/loadingServicesParams/selectors';
import { useConfig } from '../../context';

type OrderServiceFragment =
	| GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
	| GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;

export const useServicePageProps = (typeForListenEvent?: CombinedSelectedServiceType) => {
	const order = useSelector(getOrder);
	const passengers = useSelector(getPassengerStateCompatible);
	const passengerLabels = useSelector(getPassengerLabels);
	const [openedModal, setOpenedModal] = useState<ServiceType>(null);

	const [callbackAfterClose, setCallbackAfterClose] = useState<() => void>(null);
	const [analyticsMeta, setAnalyticsMeta] = useState<ServiceMeta>(null);

	const toggleModal = (type?: ServiceType) => {
		if (!!type) {
			setOpenedModal(type);
		} else {
			setAnalyticsMeta(null);
			setOpenedModal(null);

			if (callbackAfterClose) {
				callbackAfterClose();
			}
		}
	};

	useEffect(() => {
		const eventHandler = (event: CustomEvent<ServiceOpenEvent>) => {
			if (event.detail.service.type === typeForListenEvent) {
				setAnalyticsMeta(event.detail.meta);

				switch (event.detail.service.type) {
					case OrderAdditionalServiceGdsServiceServiceType.Seat:
						toggleModal(ServiceType.Seats);
						setCallbackAfterClose(() => event.detail.callback);
						break;
					case OrderAdditionalServiceGdsServiceServiceType.Baggage:
						toggleModal(ServiceType.Baggage);
						setCallbackAfterClose(() => event.detail.callback);
						break;
					case OrderAdditionalServiceGdsServiceServiceType.Meal:
						toggleModal(ServiceType.Meal);
						setCallbackAfterClose(() => event.detail.callback);
						break;
					default:
						return;
				}
			}
		};

		document.addEventListener(WEBSKY_SERVICE_OPEN_EVENT, eventHandler as EventListener);

		return () => document.removeEventListener(WEBSKY_SERVICE_OPEN_EVENT, eventHandler as EventListener);
	}, [setCallbackAfterClose, toggleModal]);

	return {
		order,
		passengers,
		passengerLabels,
		analyticsMeta,
		openedModal,
		toggleModal
	};
};

export const useCheckoutServicesCardsAvailability = () => {
	const isFfpSupported = useSelector(getIsFfpSupported);
	const aeroexpress = useSelector(getAeroexpress);
	const insurance = useSelector(getInsurances);
	const priorityBoarding = useSelector(getPriorityBoarding);
	const gdsServices = useSelector(getAvailableGdsServicesByType);
	const baggageDelivery = useSelector(getBaggageDelivery);
	const canAddUpgradeFlightClass = useSelector(getCanAddUpgradeFlightClass);
	const transferServices = useSelector(getGroundTransportationServices);
	const smsNotificationServices = useSelector(getSmsNotificationServices);
	const capsuleHotelServices = useSelector(getCapsuleHotel);
	const refundTicketServices = useSelector(getTravellerRefundableTicket);
	const animalServices = useSelector(getAnimalServices);

	const iataCode = useIataCode();
	const hasAnimal = useConfig()?.global.servicesAvailable?.animal;

	return {
		upgradeFlightClass: canAddUpgradeFlightClass,
		Insurance: insurance?.insurancePrograms?.some(insurance => insurance?.allowedPassengers?.length > 0),
		Animal: hasAnimal && animalServices?.length > 0,
		Aeroexpress: aeroexpress?.trips?.length > 0,
		Transfer: transferServices?.length > 0,
		PriorityBoarding: priorityBoarding?.length > 0,
		BaggageDelivery: baggageDelivery?.length > 0,
		RefundableTicket: refundTicketServices?.length > 0,
		SMS: smsNotificationServices?.length > 0,
		CapsuleHotel: capsuleHotelServices?.length > 0,
		Loyalty: isFfpSupported,
		...gdsServices,
		Baggage: iataWithAdditionalQueryForServices.includes(iataCode) || gdsServices.Baggage,
		Seat: iataWithAdditionalQueryForServices.includes(iataCode) || gdsServices.Seat
	};
};

export const useOrderService = (
	orderServices: OrderServiceFragment[],
	type: OrderAdditionalServiceGdsServiceServiceType,
	isAllSegments?: boolean
) => {
	const dispatch = useDispatch();

	const segments = useSelector(getSegments);
	const travellers = useSelector(getTravellers);
	const passengers = useSelector(getPassengerState);
	const selectedServices = useSelector(getSelectedServicesState);
	const isLoading = useSelector(getIsLoadingServiceByParams(orderServices[0]));

	const [opened, setOpened] = React.useState(false);

	const onOpen = () => setOpened(true);
	const onClose = () => setOpened(false);

	const isSelected = React.useMemo(() => {
		return passengers.some(passenger =>
			orderServices.some(service => selectedServices[passenger.id].hasOwnProperty(service.id))
		);
	}, [passengers, selectedServices]);

	const unconfirmedServices = React.useMemo(() => {
		let services: ServicePassengerSelected[] = [];

		if (Object.keys(selectedServices ?? {}).length) {
			for (const passengerId in selectedServices) {
				for (const serviceId in selectedServices[passengerId]) {
					const traveller = travellers.find(traveller => traveller.id === passengerId);

					if (!traveller) {
						continue;
					}

					const service = traveller?.services?.gdsServices?.services?.find(
						service => service.serviceId === serviceId
					);

					if (!service) {
						continue;
					}

					const isUnconfirmed = service.products?.some(product => isUnconfirmedService(product.status));

					if (isUnconfirmed) {
						services = [
							...services,
							{
								passengerId,
								serviceId: service.serviceId,
								segmentIds: service.segmentIds,
								count: service.count
							}
						];
					}
				}
			}
		}

		return services;
	}, [travellers, selectedServices]);

	const onSaveHandler = useCallback(
		(newSelectedServices: ServicePassengerSelected[]) => {
			batch(() => {
				dispatch(
					setMultipleSelectedServices(
						newSelectedServices.map(selectedService => ({
							service: {
								id: selectedService.serviceId,
								type
							},
							serviceId: selectedService.serviceId,
							segmentIds: isAllSegments ? selectedService.segmentIds : undefined,
							segmentId: isAllSegments ? undefined : selectedService.segmentIds[0],
							passengerId: selectedService.passengerId,
							count: selectedService.count,
							allowedSegments: selectedService.allowedSegments
						}))
					)
				);
			});

			onClose();
		},
		[dispatch, onClose, isAllSegments]
	);

	const onRemoveHandler = useCallback(
		(services: ServicePassengerSelected[]) => {
			batch(() => {
				services.forEach(service => {
					dispatch(
						setServiceAction({
							service: {
								id: service.serviceId,
								type
							},
							serviceId: service.serviceId,
							segmentId: service.segmentIds[0],
							passengerId: service.passengerId,
							count: 0
						})
					);
				});
			});

			onClose();
		},
		[dispatch, onClose]
	);

	const onToggleHandler = useCallback(() => {
		console.log('onToggleHandler');

		const services = travellers.map<ServicePassengerSelected>(traveller => ({
			passengerId: traveller.id,
			segmentIds: segments.map(segment => segment.id),
			serviceId: orderServices[0]?.id,
			count: 1
		}));

		if (isSelected) {
			onRemoveHandler(services);
		} else {
			onSaveHandler(services);
		}
	}, [orderServices, segments, travellers, isSelected]);

	const onRemoveCartService = React.useCallback<OnDeleteItemType>(
		(_, payload) => {
			const servicePayload = payload as CartServiceGds;
			const servicesToDelete = getVipServicesToDelete(servicePayload, travellers);

			onRemoveHandler(servicesToDelete);
		},
		[travellers]
	);

	const availablePassengers = React.useMemo(
		() => passengers.filter(passenger => passenger.type !== AviaPassengerType.INF),
		[passengers]
	);

	const isDifferentPrices = React.useMemo(
		() => new Set<number>(orderServices.map(service => service.price.amount)).size > 1,
		[passengers, orderServices]
	);

	const passengerPrices = React.useMemo(() => getPassengerPricesMap(passengers, orderServices, isDifferentPrices), [
		passengers,
		orderServices,
		isDifferentPrices
	]);

	const renderSubheader = React.useCallback(
		() => <PassengerPrices prices={passengerPrices} isDifferentPrices={isDifferentPrices} />,
		[passengerPrices, isDifferentPrices]
	);

	const renderPassenger = useCallback<Renderers['renderPassenger']>(
		({ passenger, ...props }) => {
			const price = orderServices?.find(service => service.id === passenger.serviceId)?.price;

			return <PassengerPriceControl {...props} passenger={passenger} price={price} />;
		},
		[orderServices, type]
	);

	const minPrice = React.useMemo(() => {
		return getMinPrice(passengerPrices, item => item.price);
	}, [isSelected, passengerPrices]);

	return {
		isSelected,
		isLoading,
		selectedServices,
		unconfirmedServices,
		renderSubheader,
		renderPassenger,
		onOpen,
		onClose,
		setOpened,
		onSaveHandler,
		onRemoveHandler,
		onToggleHandler,
		onRemoveCartService,
		passengers: availablePassengers,
		opened,
		getMinPrice: minPrice
	};
};

export const useAnimalService = (
	services: GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment[],
	travellers: (PassengerFragment | CheckinPassengerFragment)[]
) => {
	const dispatch = useDispatch();

	const servicesByRfisc = useMemo(() => {
		const servicesMap = new Map<string, BaggageFragment[]>();

		services.forEach(service => {
			const allowedSegmentsKey = service.allowedSegments
				.reduce((acc, ids) => [...acc, ids.join('')], [])
				.join('-');

			const allowedPassengersKey = service.allowedPassengers
				.reduce((acc, ids) => [...acc, ids.join('')], [])
				.join('-');

			// key - rfisc/segmentIds/passengerIds
			const key = [service.rfisc, allowedSegmentsKey, allowedPassengersKey].join('/');
			const baggageService = (service as unknown) as BaggageFragment;

			servicesMap.set(key, [...(servicesMap.get(key) ?? []), baggageService]);
		});

		return [...servicesMap.values()].map(services => {
			let actualService: BaggageFragment = null;

			services.forEach(service => {
				if (!actualService || actualService?.id < service.id) {
					actualService = service;
				}
			});

			return actualService;
		});
	}, [services]);

	const onSave = useCallback(
		(selectedServices: SelectedService[]) => {
			const selectedServicesToAdd = selectedServices.filter(service => service.count > 0);
			const selectedServicesToRemove = selectedServices.filter(service => service.count === 0);

			if (selectedServicesToAdd.length) {
				dispatch(
					setMultipleSelectedServices(
						selectedServicesToAdd.map(service => ({
							segmentId: service.segmentId,
							allowedSegments: [service.segmentIds],
							passengerId: service.passengerId,
							serviceId: service.serviceId,
							count: service.count,
							service: {
								id: service.serviceId,
								segmentId: service.segmentId,
								type: OrderAdditionalServiceGdsServiceServiceType.Baggage
							},
							extraValues: service.extraValues
						}))
					)
				);
			}

			if (selectedServicesToRemove.length) {
				selectedServicesToRemove.forEach(service => {
					dispatch(
						removeServiceAction({
							segmentId: service.segmentId,
							segmentIds: service.segmentIds,
							passengerId: service.passengerId,
							serviceId: service.serviceId,
							count: 0,
							service: {
								id: service.serviceId,
								segmentId: service.segmentId,
								type: OrderAdditionalServiceGdsServiceServiceType.Baggage
							}
						})
					);
				});
			}
		},
		[travellers, dispatch]
	);

	const onClear = useCallback(
		(selectedServices: SelectedService[]) => {
			selectedServices.forEach(service => {
				dispatch(
					removeServiceAction({
						segmentId: service.segmentId,
						segmentIds: service.segmentIds,
						passengerId: service.passengerId,
						serviceId: service.serviceId,
						count: 0,
						service: {
							id: service.serviceId,
							segmentId: service.segmentId,
							type: OrderAdditionalServiceGdsServiceServiceType.Baggage
						}
					})
				);
			});
		},
		[dispatch]
	);

	const minPrice: MoneyType = useMemo(() => {
		if (servicesByRfisc.length) {
			return getMinPrice(servicesByRfisc, service => {
				const maxSegmentCount = Math.max(...service.allowedSegments.map(ids => ids.length));

				return { ...service.price, amount: service.price.amount * maxSegmentCount };
			});
		}

		return null;
	}, [servicesByRfisc]);

	const totalPrice: MoneyType = useMemo(() => getAnimalTotalPrice(services, travellers), [services, travellers]);

	return {
		servicesByRfisc,
		minPrice,
		totalPrice,
		onSave,
		onClear
	};
};

export const useAdditionalQueryForServices = (props?: QueryHookOptions) => {
	const order = useSelector(getOrder);
	const dispatch = useDispatch();
	const onCompleted = (data: AdditionalServicesQuery) => {
		props?.onCompleted?.(data);
	};

	const [load, { data, loading }] = useAdditionalServicesLazyQuery({
		variables: { id: order.id },
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			onCompleted(data);
		}
	});

	React.useEffect(() => {
		if (data?.AdditionalServices) {
			dispatch(
				fillOrder({
					...order,
					additionalServices: data.AdditionalServices
				})
			);
		}
	}, [data]);

	return {
		loadAndDispatch: load,
		isLoading: loading,
		data
	};
};

export const useAdditonalQueryForSeatMap = () => {
	const order = useSelector(getOrder);
	const dispatch = useDispatch();

	const [load, { data, loading }] = useSeatMapLazyQuery({
		variables: { id: order.id },
		fetchPolicy: 'no-cache'
	});

	React.useEffect(() => {
		if (data?.SeatMap) {
			dispatch(
				fillOrder({
					...order,
					flight: {
						...order.flight,
						seatMap: {
							...order.flight.seatMap,
							...data.SeatMap
						}
					}
				})
			);
		}
	}, [data]);

	return {
		loadAndDispatch: load,
		isLoading: loading,
		data
	};
};

export const iataWithAdditionalQueryForServices = ['DV']; // https://youtrack.websky.aero/issue/WEBSKY-5717
